import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Tooltip,
  Alert,
  IconButton,
} from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import CloseIcon from '@mui/icons-material/Close';
import {
  useDurationSchedule,
  useRecurrence,
  useFormsAndReminders,
  useDirtyStateTracking,
  useAppointmentFormValidation,
  useAppointmentSubmission,
  useAppointmentFormReset,
  useTelehealth,
  AppointmentFormFields,
  CustomRecurrenceModal,
  DirtyChangesAlert,
  Appointment as AppointmentResponse,
  SeriesResponse,
} from '@features/appointments';
import { useTemplatesList } from '@features/user';
import { Patient } from '../../../types/types';
import { useAppointmentTypes, AppointmentTypeModal } from '@features/appointment-types';
import { useEventTracking } from '@services/statsig';
import { useOrgScheduleFilters } from '@features/appointments';
import { useOrgScheduleAccess } from '@features/appointments';

interface AddAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  initialScheduledTime?: Date;
  initialDuration?: number;
  initialPatient?: Patient;
  onAppointmentCreated?: (appointment: AppointmentResponse | SeriesResponse) => void;
}

const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
  open,
  onClose,
  initialScheduledTime,
  initialDuration,
  initialPatient,
  onAppointmentCreated,
}) => {
  const templatesList = useTemplatesList();
  const [enterKeyReleased, setEnterKeyReleased] = useState(true);
  const { appointment } = useEventTracking();

  // Org schedule admin state
  const { isOrgViewActive } = useOrgScheduleFilters();
  const { currentUserId } = useOrgScheduleAccess();
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(
    isOrgViewActive ? currentUserId : undefined,
  );

  // Patient state
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(initialPatient);

  // Notes state
  const [notes, setNotes] = useState('');

  // Track source for analytics
  const [modalOpenSource, setModalOpenSource] = useState<string>('unknown');

  // State for new appointment type modal
  const [showNewAppointmentTypeModal, setShowNewAppointmentTypeModal] = useState(false);

  // Get appointment types for auto-selection (use selected provider in org mode)
  const { appointmentTypes } = useAppointmentTypes(
    undefined,
    isOrgViewActive ? selectedProviderId : undefined,
  );

  // Use modular hooks
  const durationSchedule = useDurationSchedule({
    initialDate: initialScheduledTime,
    initialDuration: initialDuration,
  });

  const recurrence = useRecurrence();

  const telehealth = useTelehealth();

  // Initialize dirty tracking first without dependencies
  const dirtyTrackingRef = React.useRef<any>(null);

  const formsAndReminders = useFormsAndReminders({
    // templateId: templatesList?.[0]?.template_id || null,
    onMarkDirty: field => dirtyTrackingRef.current?.markDirty(field),
  });

  const dirtyTracking = useDirtyStateTracking({
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    selectedDuration: durationSchedule.selectedDuration,
    currentTelehealth: telehealth.telehealth,
    currentForms: formsAndReminders.selectedForms,
    currentReminders: formsAndReminders.selectedReminders,
  });

  // Update ref
  dirtyTrackingRef.current = dirtyTracking;

  // Form validation
  const validation = useAppointmentFormValidation({
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    mode: 'create',
  });

  // Form submission
  const submission = useAppointmentSubmission({
    mode: 'create',
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    recurrence,
    templateId: templatesList?.[0]?.template_id || null,
    selectedForms: formsAndReminders.selectedForms,
    selectedReminders: formsAndReminders.selectedReminders,
    telehealth: telehealth.telehealth,
    notes: notes || undefined,
    hasDirtyChanges: dirtyTracking.showDirtyAlert,
    providerId: isOrgViewActive ? selectedProviderId : undefined,
    onAppointmentCreated,
  });

  // Form reset
  const { resetForm: resetFormData } = useAppointmentFormReset({
    durationSchedule,
    recurrence,
    dirtyTracking,
    onPatientReset: () => setSelectedPatient(undefined),
    onAdditionalReset: () => setShowNewAppointmentTypeModal(false),
  });

  // Coordinate between hooks - duration changes mark as dirty
  const handleDurationChange = (duration: number) => {
    durationSchedule.handleDurationSelect(duration);
    dirtyTracking.markDirty('duration');
  };

  // Coordinate between hooks - telehealth changes mark as dirty
  const handleTelehealthChange = (checked: boolean) => {
    telehealth.handleTelehealthChange(checked);
    dirtyTracking.markDirty('telehealth');
  };

  // Set initial patient when it changes
  useEffect(() => {
    if (initialPatient) {
      setSelectedPatient(initialPatient);
    }
  }, [initialPatient]);

  // Track modal opening
  useEffect(() => {
    if (open) {
      // Determine source based on initial props
      const source = initialPatient
        ? 'patient'
        : initialScheduledTime
          ? 'calendar'
          : 'quick_action';
      setModalOpenSource(source);
      appointment.modalOpened({ source, is_org_schedule: isOrgViewActive });
    }
  }, [open]);

  // Initialize date and time when modal opens
  useEffect(() => {
    if (open && !initialScheduledTime && !durationSchedule.date) {
      // Set smart defaults for date and time if not provided
      const now = new Date();

      // Calculate next 15-minute interval from now
      const currentMinutes = now.getMinutes();
      const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

      // Create the next appointment time
      const nextAppointmentTime = new Date(now);
      nextAppointmentTime.setMinutes(roundedMinutes, 0, 0); // Set seconds and milliseconds to 0

      // If rounding put us in the next hour, the Date object handles this automatically
      // If we're already at the end of the day, it will roll to the next day

      // Set date (could be today or tomorrow if we rolled over)
      const dateString = nextAppointmentTime.toISOString().split('T')[0];
      const timeString = nextAppointmentTime.toTimeString().substring(0, 5); // HH:mm format

      durationSchedule.setDate(dateString);
      durationSchedule.setStartTime(timeString);
    }
  }, [open, initialScheduledTime, durationSchedule]);

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      const timeoutId = setTimeout(() => {
        resetForm();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      // When modal opens, ensure patient is set from props
      if (initialPatient && !selectedPatient) {
        setSelectedPatient(initialPatient);
      }
    }
  }, [open, initialPatient]);

  // Auto-populate defaults from appointment type when not dirty
  useEffect(() => {
    if (!formsAndReminders.selectedAppointmentType) return;

    // Handle duration - check for conflict between initialDuration and appointment type default
    if (
      !dirtyTracking.isDirty('duration') &&
      formsAndReminders.selectedAppointmentType.default_duration
    ) {
      const appointmentTypeDefault = formsAndReminders.selectedAppointmentType.default_duration;
      const currentDuration = durationSchedule.selectedDuration;

      // If we have an initialDuration (from props) that differs from appointment type default
      if (initialDuration && initialDuration !== appointmentTypeDefault) {
        // Mark duration as dirty to show the conflict alert - this preserves the initialDuration
        dirtyTracking.markDirty('duration');
      } else if (currentDuration !== appointmentTypeDefault) {
        // No conflict or no initialDuration, apply the appointment type default if it's different
        durationSchedule.handleDurationSelect(appointmentTypeDefault);
      }
    }

    // Auto-populate forms if not dirty
    if (!dirtyTracking.isDirty('forms')) {
      formsAndReminders.populateFormsFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate reminders if not dirty
    if (!dirtyTracking.isDirty('reminders')) {
      formsAndReminders.populateRemindersFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate telehealth if not dirty
    if (
      !dirtyTracking.isDirty('telehealth') &&
      formsAndReminders.selectedAppointmentType.telehealth !== undefined
    ) {
      telehealth.setTelehealth(formsAndReminders.selectedAppointmentType.telehealth ?? false);
    }
  }, [
    formsAndReminders.selectedAppointmentType,
    initialDuration,
    durationSchedule.selectedDuration,
  ]);

  // Auto-select first appointment type when types load, or re-select if current type is stale
  useEffect(() => {
    if (!appointmentTypes || appointmentTypes.length === 0) return;

    const currentType = formsAndReminders.selectedAppointmentType;

    // No type selected → auto-select first
    if (!currentType) {
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
      return;
    }

    // Current type not in the new list (e.g. provider switched) → select first from new list
    const stillValid = appointmentTypes.some(
      t => t.encounter_type_id === currentType.encounter_type_id,
    );
    if (!stillValid) {
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
    }
  }, [appointmentTypes, formsAndReminders.selectedAppointmentType]);

  // Handle creating a new encounter
  const handleCreateEncounter = async () => {
    if (!validation.isFormValid) {
      return;
    }

    const startTime = Date.now();
    await submission.submit();

    // Reset form and close modal if successful
    if (!submission.error) {
      // Track successful creation
      appointment.create({
        appointment_type_id: formsAndReminders.selectedAppointmentType?.encounter_type_id,
        duration_minutes: durationSchedule.selectedDuration,
        is_recurring: recurrence.recurrence !== 'never',
        recurrence_pattern: recurrence.recurrence !== 'never' ? recurrence.recurrence : undefined,
        is_telehealth: telehealth.telehealth,
        has_forms: formsAndReminders.selectedForms && formsAndReminders.selectedForms.length > 0,
        forms_count: formsAndReminders.selectedForms?.length || 0,
        has_reminders:
          formsAndReminders.selectedReminders && formsAndReminders.selectedReminders.length > 0,
        reminders_count: formsAndReminders.selectedReminders?.length || 0,
        source: modalOpenSource,
        is_org_schedule: isOrgViewActive,
        target_provider_id: isOrgViewActive ? selectedProviderId : undefined,
      });

      // Track note addition if notes were included
      if (notes.trim()) {
        appointment.noteAdded({
          appointment_id: 'new',
          note_length: notes.trim().length,
          is_org_schedule: isOrgViewActive,
          target_provider_id: isOrgViewActive ? selectedProviderId : undefined,
        });
      }

      resetForm();
      onClose();
    } else {
      // Track creation failure
      appointment.creationFailed({
        error_type: 'submission_error',
        error_message: submission.error,
        is_org_schedule: isOrgViewActive,
      });
    }
  };

  // Reset the form and all modal state
  const resetForm = () => {
    resetFormData();
    setSelectedPatient(undefined);
    setNotes('');
    setSelectedProviderId(isOrgViewActive ? currentUserId : undefined);
  };

  // Handle closing the modal
  const handleClose = () => {
    // Track abandonment if closing without creating
    appointment.creationAbandoned({
      source: modalOpenSource,
      had_patient_selected: !!selectedPatient,
      had_type_selected: !!formsAndReminders.selectedAppointmentType,
      is_org_schedule: isOrgViewActive,
    });

    resetForm();
    onClose();
  };

  // Handle provider selection (org schedule admin)
  const handleProviderSelect = (providerId: string) => {
    setSelectedProviderId(providerId);
    // Reset appointment type since types are per-provider
    formsAndReminders.handleAppointmentTypeSelect(undefined);
  };

  // Handle patient selection
  const handlePatientSelect = (patient: Patient | undefined) => {
    setSelectedPatient(patient);
  };

  // Wrap appointment type selection to add tracking
  const handleAppointmentTypeSelect = (appointmentType: any) => {
    formsAndReminders.handleAppointmentTypeSelect(appointmentType);
    if (appointmentType) {
      appointment.typeSelected({
        appointment_type_id: appointmentType.encounter_type_id,
        color_enum: appointmentType.color,
      });
    }
  };

  // Wrap recurrence change to track when recurring is enabled
  const handleRecurrenceChange = (value: string) => {
    recurrence.handleRecurrenceChange(value);
    if (value !== 'never') {
      appointment.recurringEnabled({ recurrence_pattern: value });
    }
  };

  // Handle dirty alert actions
  const handleUpdateAppointmentType = async () => {
    try {
      await formsAndReminders.handleUpdateAppointmentType(
        durationSchedule.selectedDuration,
        telehealth.telehealth,
      );
      dirtyTracking.resetDirtyState();
    } catch (error) {
      console.error('Failed to update appointment type:', error);
    }
  };

  // Handle Enter key press and release to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (
        enterKeyReleased &&
        !event.shiftKey &&
        !event.ctrlKey &&
        validation.isFormValid &&
        !submission.isSubmitting
      ) {
        event.preventDefault();
        handleCreateEncounter();
      }
      setEnterKeyReleased(false);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setEnterKeyReleased(true);
    }
  };

  // Don't render anything if modal is not open to prevent initialization issues
  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>New Appointment</DialogTitle>

        <DialogContent
          dividers
          sx={{
            // overflowY: "auto",
            // paddingBottom: 2,
            pt: 1,
            px: 2,
            pb: 3,
            // flex: 1,
          }}
        >
          <Stack spacing={3} marginTop={2}>
            {submission.error && <Alert severity="error">{submission.error}</Alert>}
            <AppointmentFormFields
              // Provider (org schedule admin)
              showProviderPicker={isOrgViewActive}
              selectedProviderId={selectedProviderId}
              onProviderSelect={handleProviderSelect}
              providerIdForTypes={isOrgViewActive ? selectedProviderId : undefined}
              // Patient
              selectedPatient={selectedPatient}
              onPatientSelect={handlePatientSelect}
              autoFocusPatientPicker={open && !initialPatient}
              // Appointment Type
              selectedAppointmentType={formsAndReminders.selectedAppointmentType}
              onAppointmentTypeSelect={handleAppointmentTypeSelect}
              // Duration Schedule
              date={durationSchedule.date}
              startTime={durationSchedule.startTime}
              endTime={durationSchedule.endTime}
              selectedDuration={durationSchedule.selectedDuration}
              isCustomDuration={durationSchedule.isCustomDuration}
              onDateChange={durationSchedule.handleDateChange}
              onStartTimeChange={durationSchedule.handleStartTimeChange}
              onEndTimeChange={durationSchedule.handleEndTimeChange}
              onDurationSelect={handleDurationChange}
              onCustomDurationSelect={durationSchedule.handleCustomDuration}
              // Recurrence
              recurrence={recurrence.recurrence}
              customRecurrenceRule={recurrence.customRecurrenceRule}
              onRecurrenceChange={handleRecurrenceChange}
              onCustomRecurrenceClick={() => recurrence.setIsCustomRecurrenceModalOpen(true)}
              getRecurrenceText={recurrence.getRecurrenceText}
              // Telehealth
              telehealth={telehealth.telehealth}
              onTelehealthChange={handleTelehealthChange}
              // Forms and Reminders
              selectedForms={formsAndReminders.selectedForms}
              selectedReminders={formsAndReminders.selectedReminders}
              onAddForm={formsAndReminders.handleAddForm}
              onUpdateForm={formsAndReminders.handleUpdateForm}
              onRemoveForm={formsAndReminders.handleRemoveForm}
              onAddReminder={formsAndReminders.handleAddReminder}
              onUpdateReminder={formsAndReminders.handleUpdateReminder}
              onRemoveReminder={formsAndReminders.handleRemoveReminder}
              // Notes
              notes={notes}
              onNotesChange={setNotes}
              // Mode
              mode="create"
              showRecurrence={true}
              // Keyboard events
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 0,
          }}
        >
          <DirtyChangesAlert
            show={dirtyTracking.showDirtyAlert}
            appointmentTypeName={formsAndReminders.selectedAppointmentType?.encounter_type || ''}
            changes={dirtyTracking.changes}
            selectedAppointmentType={formsAndReminders.selectedAppointmentType}
            currentDuration={durationSchedule.selectedDuration}
            currentTelehealth={telehealth.telehealth}
            currentForms={formsAndReminders.selectedForms}
            currentReminders={formsAndReminders.selectedReminders}
            currentTemplate={
              templatesList?.find(
                t => t.template_id === formsAndReminders.selectedAppointmentType?.template_id,
              ) || null
            }
            onUpdateAppointmentType={handleUpdateAppointmentType}
            onCreateNew={() => setShowNewAppointmentTypeModal(true)}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Cancel</Button>
            <Tooltip
              title={
                !validation.isFormValid
                  ? validation.getValidationMessage()
                  : validation.isFormValid && !submission.isSubmitting
                    ? 'Press Enter to create'
                    : ''
              }
              placement="top"
            >
              <span>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="contained"
                    onClick={handleCreateEncounter}
                    disabled={!validation.isFormValid || submission.isSubmitting}
                    sx={{
                      color: 'white',
                      paddingRight:
                        validation.isFormValid && !submission.isSubmitting ? '30px' : '16px',
                    }}
                  >
                    {submission.isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                  {validation.isFormValid && !submission.isSubmitting && (
                    <KeyboardReturnIcon
                      fontSize="small"
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '1rem',
                        opacity: 0.7,
                        color: 'white',
                      }}
                    />
                  )}
                </Box>
              </span>
            </Tooltip>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Custom Recurrence Modal */}
      <CustomRecurrenceModal
        open={recurrence.isCustomRecurrenceModalOpen}
        onClose={() => recurrence.setIsCustomRecurrenceModalOpen(false)}
        onSave={recurrence.handleCustomRecurrenceSave}
        initialRule={recurrence.customRecurrenceRule || undefined}
      />

      {/* New Appointment Type Modal */}
      <AppointmentTypeModal
        type="create"
        open={showNewAppointmentTypeModal}
        onClose={() => setShowNewAppointmentTypeModal(false)}
        onSave={newAppointmentType => {
          handleAppointmentTypeSelect(newAppointmentType);
          dirtyTracking.resetDirtyState();
          setShowNewAppointmentTypeModal(false);
        }}
        initialName={formsAndReminders.selectedAppointmentType?.encounter_type + ' (Copy)' || ''}
        defaultValues={{
          basedOnAppointmentType: formsAndReminders.selectedAppointmentType,
          overrides: {
            duration: durationSchedule.selectedDuration,
            telehealth: telehealth.telehealth,
            forms: formsAndReminders.selectedForms,
            reminders: formsAndReminders.selectedReminders,
          },
        }}
      />
    </>
  );
};

export default AddAppointmentModal;
